

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { FaPlus, FaMinus, FaTrash, FaPrint, FaDownload } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "./MenuCartSidebar.css";

Modal.setAppElement('#root');

const MenuCartSidebar = () => {
    const navigate = useNavigate();
    const [cartData, setCartData] = useState({ cartItems: [] });
    const [quantities, setQuantities] = useState({});
    const [grandTotal, setGrandTotal] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [userId, setUserId] = useState(localStorage.getItem("userId") || 3);
    const [loading, setLoading] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
    const [savedDetails, setSavedDetails] = useState(null);

    // New states for receipt functionality
    const [showReceipt, setShowReceipt] = useState(false);
    const [orderData, setOrderData] = useState({});
    const [bannerData, setBannerData] = useState(null);
    const printRef = useRef();
    const receiptRef = useRef();


    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const fetchOrderDetails = async (orderId) => {
        try {
            const authToken = localStorage.getItem("authToken");
            if (!authToken) {
                console.error("❌ No auth token found in localStorage");
                return;
            }

            const response = await axios.get(
                `https://backend.maamaas.com/food/api/orders/order/${orderId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                setOrderData(response.data);
                return response.data;
            }
        } catch (error) {
            console.error("❌ Failed to fetch order details:", error.response?.data || error.message);
        }
    };

    const fetchBannerData = async () => {
        const vendorId = localStorage.getItem("vendorId");
        if (!vendorId) return;

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `https://backend.maamaas.com/food/banner/vendor/${vendorId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setBannerData({
                ...response.data,
                companyName: response.data.companyName || "MAAMAAS HOUSE",
            });
        } catch (err) {
            console.error("Error fetching banner data:", err);
        }
    };

    const handlePlaceOrder = async (paymentMethodParam = null) => {
        const vendorId = localStorage.getItem("vendorId");
        const token = localStorage.getItem("authToken");
        const cartId = localStorage.getItem("cartId");

        if (!userId) {
            alert("User ID not found. Please log in again.");
            return;
        }

        // Use the parameter if provided, otherwise use state
        const paymentMethod = paymentMethodParam || selectedPaymentMethod;

        if (!paymentMethod) {
            alert("Please select a payment method");
            return;
        }

        try {
            localStorage.removeItem("orderId");

            // 🟣 ONLINE PAYMENT
            if (paymentMethod === "Online_Payment") {
                try {
                    const isRazorpayLoaded = await loadRazorpayScript();
                    if (!isRazorpayLoaded) {
                        alert("❌ Razorpay SDK failed to load.");
                        setLoading(false);
                        return;
                    }

                    const amountInPaise = Math.round(grandTotal * 100);
                    const displayAmount = amountInPaise / 100;

                    // 🔹 Step 1: Create Razorpay Order via backend
                    const orderResponse = await fetch(
                        "https://backend.maamaas.com/food/api/payments/create-order",
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                amount: displayAmount,
                                phoneNumber: "9745896321",
                                currency: "INR",
                                receipt: `order_${Date.now()}`,
                                notes: {
                                    customer_name: savedDetails?.name || "John Doe",
                                    order_reference: `WEB_ORDER_${Math.floor(Math.random() * 10000)}`,
                                },
                            }),
                        }
                    );

                    if (!orderResponse.ok) throw new Error("❌ Failed to create Razorpay order");
                    const orderData = await orderResponse.json();

                    // 🔹 Step 2: Open Razorpay Checkout
                    const options = {
                        key: "rzp_live_RU5whSMu9rPV7s",
                        amount: amountInPaise,
                        currency: "INR",
                        order_id: orderData.orderId,
                        name: "Maamaa's House",
                        description: "Order Payment",
                        prefill: {
                            name: savedDetails?.name || "vamshi",
                            email: savedDetails?.email || "vamshikvky500@gmail.com",
                            contact: savedDetails?.mobileNumber || "9652030425",
                        },
                        theme: { color: "#3399cc" },

                        handler: async function (response) {
                            try {
                                // 🔹 Capture payment payload
                                const capturePayload = {
                                    paymentId: response.razorpay_payment_id,
                                    amount: amountInPaise / 100,
                                    currency: "INR",
                                    receipt: `receipt_${Date.now()}`,
                                };

                                // 🔹 Call capture API
                                const captureRes = await fetch(
                                    "https://backend.maamaas.com/food/api/payments/capture",
                                    {
                                        method: "POST",
                                        headers: {
                                            "Content-Type": "application/json",
                                            Authorization: `Bearer ${token}`,
                                        },
                                        body: JSON.stringify(capturePayload),
                                    }
                                );

                                const captureData = await captureRes.json();

                                if (!captureRes.ok || captureData.status !== "captured") {
                                    alert("❌ Payment capture failed!");
                                    return;
                                }

                                // 🔹 Backend order creation
                                const backendOrderParams = {
                                    vendorId: vendorId,
                                    paymentMethod: "Online_Payment",
                                    razorpayPaymentId: response.razorpay_payment_id,
                                    razorpayOrderId: 1,
                                    phoneNumber: savedDetails?.mobileNumber || "8688037081",
                                };
                                const responseOrder = await axios.post(
                                    `https://backend.maamaas.com/food/api/orders/orders/vendor/create/${localStorage.getItem("cartId")}`,
                                    "",
                                    {
                                        params: backendOrderParams,
                                        headers: { Authorization: `Bearer ${token}` },
                                        validateStatus: false,
                                    }
                                );

                                if (responseOrder.status === 200 || responseOrder.status === 201) {
                                    const orderId = responseOrder.data.orderId;
                                    localStorage.setItem("orderId", orderId);

                                    // Fetch order details and show receipt in sidebar
                                    const orderDetails = await fetchOrderDetails(orderId);
                                    if (orderDetails) {
                                        setShowReceipt(true);
                                        fetchBannerData();
                                    }

                                    // alert("Payment captured and order placed successfully!");
                                } else {
                                    alert("❌ Order creation failed after payment!");
                                }

                            } catch (err) {
                                alert("❌ Something went wrong after payment.");
                            }
                        },
                    };
                    const paymentObject = new window.Razorpay(options);
                    paymentObject.open();
                    return;
                } catch (err) {
                    alert("❌ Razorpay flow failed.");
                    setLoading(false);
                    return;
                }
            }

            // // 🟢 CASH PAYMENT
            // console.log("🟢 Cash Payment Flow Started");

            // const cashParams = {
            //     vendorId,
            //     paymentMethod: "Cash"
            // };

            // const cashRes = await axios.post(
            //     `https://backend.maamaas.com/food/api/orders/orders/vendor/create/${cartId}`,
            //     "",
            //     {
            //         params: cashParams,
            //         headers: { Authorization: `Bearer ${token}` }
            //     }
            // );

            // if (cashRes.data.orderId) {
            //     const orderId = cashRes.data.orderId;
            //     localStorage.setItem("orderId", orderId);

            //     // Fetch order details and show receipt in sidebar
            //     const orderDetails = await fetchOrderDetails(orderId);
            //     if (orderDetails) {
            //         setShowReceipt(true);
            //         fetchBannerData();
            //     }

            //     alert("Order Placed Successfully with CASH Payment");
            //     navigate("/foodAccuontdata");
            // } else {
            //     alert("⚠️ Cash Order creation failed");
            // }
            // 🟢 CASH PAYMENT
            // 🟢 CASH PAYMENT
            console.log("🟢 Cash Payment Flow Started");

            const cashParams = {
                vendorId,
                paymentMethod: "Cash"
            };

            const cashRes = await axios.post(
                `https://backend.maamaas.com/food/api/orders/orders/vendor/create/${cartId}`,
                "",
                {
                    params: cashParams,
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (cashRes.data.orderId) {
                const orderId = cashRes.data.orderId;
                localStorage.setItem("orderId", orderId);

                // Store order data in localStorage for receipt to show later
                localStorage.setItem("pendingReceiptOrderId", orderId);
                localStorage.setItem("pendingReceiptPaymentMethod", "Cash");
                localStorage.setItem("pendingReceiptShow", "true");

                // Clear cart locally
                setCartData({ cartItems: [] });
                setQuantities({});
                setGrandTotal(0);
                setSelectedPaymentMethod("");

                // Trigger cart refresh for other components
                triggerCartRefresh();

                alert("Order Placed Successfully with CASH Payment!");

                // Redirect to cash billing page
                navigate("/foodAccuontdata");
            } else {
                alert("⚠️ Cash Order creation failed");
            }

        } catch (error) {
            console.error("🚨 Checkout Error:", error);
            alert("Failed to proceed to checkout.");
        }
    };
    useEffect(() => {
        // Check if there's a pending receipt to show (from cash payment flow)
        const checkPendingReceipt = async () => {
            const pendingReceiptShow = localStorage.getItem("pendingReceiptShow");
            const pendingOrderId = localStorage.getItem("pendingReceiptOrderId");

            // Also check for receipt data from Cashbilling page
            const showReceipt = localStorage.getItem("showReceipt");
            const receiptOrderId = localStorage.getItem("receiptOrderId");
            const receiptOrderDataStr = localStorage.getItem("receiptOrderData");

            if ((pendingReceiptShow === "true" && pendingOrderId) ||
                (showReceipt === "true" && receiptOrderId)) {

                try {
                    let orderDetails;
                    let orderId;

                    if (showReceipt === "true" && receiptOrderDataStr) {
                        // Get data from Cashbilling page
                        orderDetails = JSON.parse(receiptOrderDataStr);
                        orderId = receiptOrderId;
                    } else {
                        // Fetch fresh order details
                        orderId = pendingOrderId;
                        orderDetails = await fetchOrderDetails(orderId);
                    }

                    if (orderDetails) {
                        setOrderData(orderDetails);
                        setShowReceipt(true);
                        await fetchBannerData();

                        // Clear all receipt flags
                        localStorage.removeItem("pendingReceiptShow");
                        localStorage.removeItem("pendingReceiptOrderId");
                        localStorage.removeItem("pendingReceiptPaymentMethod");
                        localStorage.removeItem("showReceipt");
                        localStorage.removeItem("receiptOrderId");
                        localStorage.removeItem("receiptOrderData");
                        localStorage.removeItem("receiptPaymentData");
                    }
                } catch (err) {
                    console.error("Error showing pending receipt:", err);
                }
            }
        };

        checkPendingReceipt();
    }, []);

    const handlePaymentMethodChange = async (method) => {
        // Update the state first
        setSelectedPaymentMethod(method);

        // For online payment, call handlePlaceOrder with the method directly
        if (method === "Online_Payment") {
            await handlePlaceOrder(method);
        }
        // For cash, just update the state - user will click the Place Order button
    };

    // Add this function near the top of your component
    const refreshCartAndPage = () => {
        // Clear local cart state
        setCartData({ cartItems: [] });
        setQuantities({});
        setGrandTotal(0);
        setSelectedPaymentMethod("");

        // Trigger cart refresh for other components
        triggerCartRefresh();

        // Refresh the page after a short delay
        setTimeout(() => {
            window.location.reload();
        }, 500);
    };
    useEffect(() => {
        // Initial check when component mounts
        const initializeReceipt = async () => {
            const showReceipt = localStorage.getItem("showReceipt");
            const receiptOrderId = localStorage.getItem("receiptOrderId");

            if (showReceipt === "true" && receiptOrderId) {
                // If we have receipt data, fetch order details
                const orderDetails = await fetchOrderDetails(receiptOrderId);
                if (orderDetails) {
                    setOrderData(orderDetails);
                    setShowReceipt(true);
                    await fetchBannerData();
                }
            }
        };

        initializeReceipt();
    }, []);

    // Update closeReceipt to use this function
    const closeReceipt = () => {
        setShowReceipt(false);
        setOrderData({});
        refreshCartAndPage();
    };

    // const handlePrint = () => {
    //     if (!printRef.current) return;

    //     const clone = printRef.current.cloneNode(true);
    //     clone.querySelectorAll(".receipt-buttons").forEach(btn => btn.remove());

    //     const bannerHTML = `
    //     <div style="text-align:center;margin-bottom:10px;">
    //         <h5 style="font-size:16px;text-transform:uppercase;font-weight:900;margin:0;padding:0;">
    //             ${bannerData?.companyName || "MAAMAAS HOUSE"}
    //         </h5>
    //         <div style="width:100%;border-top:3px solid black;margin:4px 0;"></div>
    //     </div>
    // `;

    //     const styledContent = `
    //     <html>
    //     <head>
    //     <title>Receipt</title>
    //     <style>
    //     @media print {
    //         @page { size: 80mm auto; margin: 0; }
    //         body { color:black !important; font-family:monospace; background:white !important; width:80mm; margin:0 auto; padding:2px; font-weight:900 !important; }
    //         h3,h5,p,dt,dd,span,div { color:black !important; font-weight:900 !important; text-align:center; }
    //         table { width:100%; border-collapse:collapse; table-layout:fixed; color:black !important; font-size:9px !important; }
    //         th, td { text-align:center !important; vertical-align:middle; padding:3px 2px !important; font-weight:900 !important; }
    //         hr { border:0; height:1px; background-color:#000; margin:4px 0; width:100%; }
    //     }
    //     </style>
    //     </head>
    //     <body>
    //     ${bannerHTML}
    //     ${clone.innerHTML}
    //     </body>
    //     </html>
    // `;

    //     const iframe = document.createElement('iframe');
    //     iframe.style.position = 'fixed';
    //     iframe.style.right = '0';
    //     iframe.style.bottom = '0';
    //     iframe.style.width = '0';
    //     iframe.style.height = '0';
    //     iframe.style.border = 'none';
    //     document.body.appendChild(iframe);

    //     const doc = iframe.contentWindow.document;
    //     doc.open();
    //     doc.write(styledContent);
    //     doc.close();

    //     iframe.contentWindow.focus();

    //     // Store the iframe reference for cleanup
    //     const iframeRef = iframe;

    //     // Use a more reliable approach with setTimeout
    //     const handleAfterPrint = () => {
    //         setTimeout(() => {
    //             // Clean up iframe
    //             if (document.body.contains(iframeRef)) {
    //                 document.body.removeChild(iframeRef);
    //             }

    //             // Refresh cart and page
    //             triggerCartRefresh();
    //             window.location.reload();
    //         }, 1000); // 1 second delay to ensure print dialog is closed
    //     };

    //     // Try multiple event listeners
    //     iframe.contentWindow.addEventListener('afterprint', handleAfterPrint);

    //     // Also listen for focus/blur events as fallback
    //     window.addEventListener('focus', function afterPrintFocus() {
    //         setTimeout(() => {
    //             window.removeEventListener('focus', afterPrintFocus);
    //             handleAfterPrint();
    //         }, 500);
    //     });

    //     iframe.contentWindow.print();
    // };
    //     const handlePrint = () => {
    //         if (!printRef.current) return;

    //         const originalContent = document.body.innerHTML;

    //         const printStyles = `
    //     <style>
    //     @media print {
    //         body {
    //             width: 80mm !important;
    //             margin: 0 auto !important;
    //             padding: 0 !important;
    //             font-family: "Courier New", monospace !important;
    //             font-size: 10px !important;
    //             line-height: 1.2 !important;
    //             font-weight:900 !important;
    //         }

    //         h1, h2, h3 {
    //             font-weight: bold;
    //             text-align: center;
    //             margin: 0;
    //             padding: 0;
    //         }

    //         .big-title {
    //             font-size: 14px !important;
    //             font-weight: bold;
    //             text-align: center;
    //             margin-bottom: 5px;
    //         }

    //         .center-text {
    //             text-align: center;
    //             font-size: 10px;
    //             font-weight: bold;
    //         }

    //         .line {
    //             border-top: 1px solid black;
    //             margin: 6px 0;
    //         }

    //         .dashed {
    //             border-top: 1px dashed black;
    //             margin: 6px 0;
    //         }

    //         table {
    //             width: 100%;
    //             font-size: 10px;
    //             border-collapse: collapse;
    //         }

    //         th{
    //             padding: 0px 0px;
    //             font-weight: bold;
    //         }
    //         td { 
    //             padding: 2px 0px;
    //             font-weight: bold;
    //         }

    //         .row {
    //             display: flex;
    //             justify-content: space-around;
    //             font-size: 10px;
    //             font-weight: bold;
    //             white-space: nowrap;
    //             width: 100%;

    //         }

    //         .grand-label {
    //             font-size: 16px;
    //             font-weight: bold;
    //         }

    //         .grand-value {
    //             font-size: 16px;
    //             font-weight: bold;
    //         }

    //         .footer {
    //             text-align: center;
    //             margin-top: 10px;
    //             font-size: 10px;
    //         }

    //         .receipt-buttons, button {
    //             display: none !important;
    //         }

    //         @page {
    //             size: 80mm auto;
    //             margin: 0;
    //         }

    //         .page-break {
    //             page-break-after: always;
    //         }
    //     }
    //     </style>
    //     `;

    //         // SIMPLE RECEIPT (Top Image)
    //         const receiptOne = `
    //         <div class="big-title">MAAMAAS</div>
    //         <div class="line"></div>

    //         <div class="row"><span>OrderID: ${orderData.orderId}</span><span>Date: ${orderData.date}</span></div>
    //         <div class="row"><span>Type: ${orderData.orderType}</span><span>Time: ${orderData.time}</span></div>

    //         <div class="center-text">Payment: ${orderData.paymentMethod}</div>

    //         <div class="line"></div>

    //         <table>
    //             <thead>
    //                 <tr>
    //                     <th style="text-align:center;">Item</th>
    //                     <th style="text-align:center;">Qty</th>
    //                 </tr>
    //             </thead>
    //             <tbody>
    //                 ${orderData.order.map(item => `
    //                     <tr>
    //                         <td style="text-align:center;">${item.dishName}</td>
    //                         <td style="text-align:center;">${item.quantity}</td>
    //                     </tr>
    //                 `).join("")}
    //             </tbody>
    //         </table>

    //         <div class="line"></div>
    //     `;

    //         // DETAILED RECEIPT (Bottom Image)
    //         const receiptTwo = `
    //         <div class="big-title">MAAMAAS</div>
    //         <div class="center-text">GSTIN: ${orderData?.vendorGstIn || "N/A"}</div>
    //         <div class="line"></div>

    //         <div class="row"><span>OrderID: ${orderData.orderId}</span><span>Date: ${orderData.date}</span></div>
    //         <div class="row"><span>Type: ${orderData.orderType}</span><span>Time: ${orderData.time}</span></div>
    //         <div class="center-text">Payment: ${orderData.paymentMethod}</div>

    //         <div class="line"></div>

    //         <table class="items-table">
    //     <thead>
    //         <tr>
    //             <th style="text-align:center;">Item</th>
    //             <th style="text-align:center;">Qty</th>
    //             <th style="text-align:center;">Price</th>
    //             <th style="text-align:center;">Total</th>
    //         </tr>
    //     </thead>
    //     <tbody>
    //         ${orderData.order.map(item => `
    //             <tr>
    //                 <td style="text-align:center;">${item.dishName}</td>
    //                 <td style="text-align:center;">${item.quantity}</td>
    //                 <td style="text-align:center;">₹${item.price.toFixed(2)}</td>
    //                 <td style="text-align:center;">₹${(item.price * item.quantity).toFixed(2)}</td>
    //             </tr>
    //         `).join("")}
    //     </tbody>
    // </table>


    //         <div class="line"></div>

    //         <div class="row"><span>SubTotal</span><span>₹${orderData.subTotal}</span></div>
    //         <div class="row"><span>Platform</span><span>₹${orderData.platformCharges}</span></div>
    //         <div class="row"><span>Service</span><span>₹${orderData.serviceCharge}</span></div>
    //         <div class="row"><span>CGST</span><span>₹${orderData.cgst}</span></div>
    //         <div class="row"><span>SGST</span><span>₹${orderData.sgst}</span></div>

    //         <div class="line"></div>

    //         <div class="row">
    //             <span class="grand-label">Grand Total</span>
    //             <span class="grand-value">₹${orderData.grandTotal}</span>
    //         </div>

    //         <div class="dashed"></div>
    //         <div class="center-text">FSSAI: ${orderData?.vendorFssai || "N/A"}</div>

    //         <div class="footer">
    //             Thank you, visit again<br/>
    //             www.maamaas.com
    //         </div>
    //     `;

    //         // FINAL PRINT CONTENT
    //         const finalHTML = `
    //         ${printStyles}
    //         ${receiptOne}
    //         <div class="page-break"></div>
    //         ${receiptTwo}
    //     `;

    //         const printWindow = window.open('', '_blank', 'width=800,height=600');
    //         printWindow.document.open();
    //         printWindow.document.write(`<html><body>${finalHTML}</body></html>`);
    //         printWindow.document.close();

    //         printWindow.onload = () => {
    //             printWindow.print();
    //             setTimeout(() => printWindow.close(), 500);
    //         };

    //         document.body.innerHTML = originalContent;
    //     };
    const handlePrint = () => {
        if (!printRef.current) return;

        const originalContent = document.body.innerHTML;

        const printStyles = `
    <style>
    @media print {
        body {
            width: 80mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
            font-family: "Courier New", monospace !important;
            font-size: 10px !important;
            line-height: 1.2 !important;
            font-weight:900 !important;
        }

        h1, h2, h3 {
            font-weight: bold;
            text-align: center;
            margin: 0;
            padding: 0;
        }

        .big-title {
            font-size: 14px !important;
            font-weight: bold;
            text-align: center;
            margin-bottom: 5px;
        }

        .center-text {
            text-align: center;
            font-size: 10px;
            font-weight: bold;
        }

        .line {
            border-top: 1px solid black;
            margin: 6px 0;
        }

        .dashed {
            border-top: 1px dashed black;
            margin: 6px 0;
        }

        table {
            width: 100%;
            font-size: 10px;
            border-collapse: collapse;
        }

        th{
            padding: 0px 0px;
            font-weight: bold;
        }
        td { 
            padding: 2px 0px;
            font-weight: bold;
        }
            
        .row {
            display: flex;
            justify-content: space-around;
            font-size: 10px;
            font-weight: bold;
            white-space: nowrap;
            width: 100%;

        }

        .grand-label {
            font-size: 16px;
            font-weight: bold;
        }

        .grand-value {
            font-size: 16px;
            font-weight: bold;
        }

        .footer {
            text-align: center;
            margin-top: 10px;
            font-size: 10px;
        }

        .receipt-buttons, button {
            display: none !important;
        }

        @page {
            size: 80mm auto;
            margin: 0;
        }

        .page-break {
            page-break-after: always;
        }
    }
    </style>
    `;

        // SIMPLE RECEIPT (Top Image)
        const receiptOne = `
        <div class="big-title">MAAMAAS</div>
        <div class="line"></div>

        <div class="row"><span>OrderID: ${orderData.orderId}</span><span>Date: ${orderData.date}</span></div>
        <div class="row"><span>Type: ${orderData.orderType}</span><span>Time: ${orderData.time}</span></div>

        <div class="center-text">Payment: ${orderData.paymentMethod}</div>

        <div class="line"></div>

        <table>
            <thead>
                <tr>
                    <th style="text-align:center;">Item</th>
                    <th style="text-align:center;">Qty</th>
                </tr>
            </thead>
            <tbody>
                ${orderData.order.map(item => `
                    <tr>
                        <td style="text-align:center;">${item.dishName}</td>
                        <td style="text-align:center;">${item.quantity}</td>
                    </tr>
                `).join("")}
            </tbody>
        </table>

        <div class="line"></div>
    `;

        // DETAILED RECEIPT (Bottom Image)
        const receiptTwo = `
        <div class="big-title">MAAMAAS</div>
        <div class="center-text">GSTIN: ${orderData?.vendorGstIn || "N/A"}</div>
        <div class="line"></div>

        <div class="row"><span>OrderID: ${orderData.orderId}</span><span>Date: ${orderData.date}</span></div>
        <div class="row"><span>Type: ${orderData.orderType}</span><span>Time: ${orderData.time}</span></div>
        <div class="center-text">Payment: ${orderData.paymentMethod}</div>

        <div class="line"></div>

        <table class="items-table">
    <thead>
        <tr>
            <th style="text-align:center;">Item</th>
            <th style="text-align:center;">Qty</th>
            <th style="text-align:center;">Price</th>
            <th style="text-align:center;">Total</th>
        </tr>
    </thead>
    <tbody>
        ${orderData.order.map(item => `
            <tr>
                <td style="text-align:center;">${item.dishName}</td>
                <td style="text-align:center;">${item.quantity}</td>
                <td style="text-align:center;">₹${item.price.toFixed(2)}</td>
                <td style="text-align:center;">₹${(item.price * item.quantity).toFixed(2)}</td>
            </tr>
        `).join("")}
    </tbody>
</table>


        <div class="line"></div>

        <div class="row"><span>SubTotal</span><span>₹${orderData.subTotal}</span></div>
        <div class="row"><span>Platform</span><span>₹${orderData.platformCharges}</span></div>
        <div class="row"><span>Service</span><span>₹${orderData.serviceCharge}</span></div>
        <div class="row"><span>CGST</span><span>₹${orderData.cgst}</span></div>
        <div class="row"><span>SGST</span><span>₹${orderData.sgst}</span></div>

        <div class="line"></div>

        <div class="row">
            <span class="grand-label">Grand Total</span>
            <span class="grand-value">₹${orderData.grandTotal}</span>
        </div>

        <div class="dashed"></div>
        <div class="center-text">FSSAI: ${orderData?.vendorFssai || "N/A"}</div>

        <div class="footer">
            Thank you, visit again<br/>
            www.maamaas.com
        </div>
    `;

        // FINAL PRINT CONTENT
        const finalHTML = `
        ${printStyles}
        ${receiptOne}
        <div class="page-break"></div>
        ${receiptTwo}
    `;

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.open();
        printWindow.document.write(`<html><body>${finalHTML}</body></html>`);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.print();

            // Listen for afterprint event
            printWindow.onafterprint = () => {
                setTimeout(() => {
                    printWindow.close();
                    window.location.reload(); // ADDED AUTO-REFRESH
                }, 500);
            };

            // Fallback in case afterprint doesn't fire
            setTimeout(() => {
                printWindow.close();
                window.location.reload(); // ADDED AUTO-REFRESH
            }, 3000);
        };

        document.body.innerHTML = originalContent;
    };
    const handleDownload = async () => {
        const receipt = receiptRef.current;
        if (!receipt) return;

        // Store original button states
        const buttons = receipt.querySelectorAll(".receipt-buttons");
        const buttonStates = [];
        buttons.forEach(btn => {
            buttonStates.push({
                element: btn,
                originalDisplay: btn.style.display
            });
            btn.style.display = "none";
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        const originalStyle = {
            height: receipt.style.height,
            overflowY: receipt.style.overflowY
        };
        receipt.style.height = "auto";
        receipt.style.overflowY = "visible";

        try {
            const canvas = await html2canvas(receipt, {
                scale: 2,
                useCORS: true,
                logging: false
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, "PNG", 0, 10, imgWidth, imgHeight);

            // Save the PDF
            pdf.save("Payment_Receipt.pdf");

            // Restore buttons immediately
            buttonStates.forEach(state => {
                state.element.style.display = state.originalDisplay;
            });
            receipt.style.height = originalStyle.height;
            receipt.style.overflowY = originalStyle.overflowY;

            // Wait a moment then refresh
            setTimeout(() => {
                triggerCartRefresh();
                window.location.reload();
            }, 500);

        } catch (err) {
            console.error("❌ Error generating PDF:", err);
            // Restore buttons on error
            buttonStates.forEach(state => {
                state.element.style.display = state.originalDisplay;
            });
            receipt.style.height = originalStyle.height;
            receipt.style.overflowY = originalStyle.overflowY;

            // Still refresh on error
            setTimeout(() => {
                triggerCartRefresh();
                window.location.reload();
            }, 500);
        }
    };

    // const handlePaymentMethodChange = async (method) => {
    //     setSelectedPaymentMethod(method);

    //     if (method === "Online_Payment") {
    //         await handlePlaceOrder();
    //     }
    // };

    const clearCart = async () => {
        const token = localStorage.getItem("authToken");
        const vendorId = localStorage.getItem("vendorId");
        const cartId = localStorage.getItem("cartId");

        if (!token || !vendorId || !cartId) {
            alert("Unable to clear cart. Missing required data.");
            return;
        }

        if (cartData.cartItems.length === 0) {
            alert("Cart is already empty!");
            return;
        }

        if (!window.confirm("Are you sure you want to clear all items from your cart?")) {
            return;
        }

        try {
            for (const item of cartData.cartItems) {
                const apiUrl = `https://backend.maamaas.com/food/api/cart/update/cart/${vendorId}/${cartId}?dishId=${item.dishId}&quantity=0`;
                await axios.put(apiUrl, null, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            setCartData({ cartItems: [] });
            setQuantities({});
            setGrandTotal(0);
            setShowReceipt(false); // Hide receipt when clearing cart

            alert("Cart cleared successfully!");
            triggerCartRefresh();

        } catch (err) {
            console.error("Error clearing cart:", err);
            alert("Failed to clear cart. Please try again.");
        }
    };

    const triggerCartRefresh = () => {
        const event = new CustomEvent('cartUpdated');
        window.dispatchEvent(event);
    }

    // const closeReceipt = () => {
    //     setShowReceipt(false);
    //     setOrderData({});
    //     // Clear cart after successful order
    //     setCartData({ cartItems: [] });
    //     setQuantities({});
    //     setGrandTotal(0);
    //     setSelectedPaymentMethod("");
    //     triggerCartRefresh();

    //     // Force a page refresh to update cart count
    //     window.location.reload();
    // };

    useEffect(() => {
        const handleCartUpdate = () => {
            setRefresh(prev => prev + 1);
        };

        window.addEventListener('cartUpdated', handleCartUpdate);
        return () => {
            window.removeEventListener('cartUpdated', handleCartUpdate);
        };
    }, []);

    useEffect(() => {
        const fetchCartData = async () => {
            const vendorId = localStorage.getItem("vendorId");
            const authToken = localStorage.getItem("authToken");

            if (!vendorId || !authToken) return;

            try {
                const response = await axios.get(
                    `https://backend.maamaas.com/food/api/cart/vendor/${vendorId}`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

                if (response.data) {
                    const data = response.data;
                    setCartData(data);
                    setGrandTotal(data.grandTotal || 0);

                    if (data.cartId) {
                        localStorage.setItem("cartId", data.cartId);
                    }

                    const updatedQuantities = (data.cartItems || []).reduce((acc, item) => {
                        acc[item.dishId] = item.quantity || 1;
                        return acc;
                    }, {});
                    setQuantities(updatedQuantities);
                }
            } catch (err) {
                console.error("Error fetching cart data:", err);
            }
        };

        fetchCartData();
    }, [refresh]);

    // Rest of your existing functions (updateQuantity, increaseQuantity, decreaseQuantity, confirmRemove, cancelRemove)
    const updateQuantity = async (dishId, newQty) => {
        const token = localStorage.getItem("authToken");
        const vendorId = localStorage.getItem("vendorId");

        const apiUrl = `https://backend.maamaas.com/food/api/cart/update/cart/${vendorId}/${cartData.cartId}?dishId=${dishId}&quantity=${newQty}`;

        try {
            const response = await axios.put(apiUrl, null, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setQuantities(prev => ({ ...prev, [dishId]: newQty > 0 ? newQty : 0 }));
            setCartData(prev => ({
                ...prev,
                cartItems: newQty > 0
                    ? prev.cartItems.map(item =>
                        item.dishId === dishId ? { ...item, quantity: newQty } : item
                    )
                    : prev.cartItems.filter(item => item.dishId !== dishId)
            }));
            setIsModalOpen(true);
            triggerCartRefresh();
            setTimeout(() => {
                if (!isHovered) setIsModalOpen(false);
            }, 20000000);
        } catch (err) {
            console.error("API Error:", err.response ? err.response.data : err);
            alert("Unable to update quantity for dishId: " + dishId);
        }
    };

    const increaseQuantity = async (dishId) => {
        const token = localStorage.getItem("authToken");
        const vendorId = localStorage.getItem("vendorId");
        if (!token || !vendorId) return alert("Auth token or vendorId missing");

        if (!cartData || !cartData.cartItems) {
            alert("Cart is empty or not loaded yet.");
            return;
        }

        const cartItem = cartData.cartItems.find(item => item.dishId === dishId);
        if (!cartItem) {
            alert("Item not found in cart. Please add it first.");
            return;
        }

        const newQty = (quantities[dishId] || 0) + 1;

        const apiUrl = `https://backend.maamaas.com/food/api/cart/update/cart/${vendorId}/${cartData.cartId}?dishId=${dishId}&quantity=${newQty}`;

        try {
            const response = await axios.put(apiUrl, null, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setQuantities(prev => ({ ...prev, [dishId]: newQty }));
            setCartData(prev => ({
                ...prev,
                cartItems: prev.cartItems.map(item =>
                    item.dishId === dishId ? { ...item, quantity: newQty } : item
                )
            }));
            setIsModalOpen(true);
            triggerCartRefresh();
            setTimeout(() => {
                if (!isHovered) setIsModalOpen(false);
            }, 20000000);
        } catch (err) {
            console.error("API Error:", err.response ? err.response.data : err);
            alert("Unable to update quantity for dishId: " + dishId);
        }
    };

    const decreaseQuantity = async (dishId) => {
        const token = localStorage.getItem("authToken");
        const vendorId = localStorage.getItem("vendorId");
        const currentQty = quantities[dishId] || 0;
        if (currentQty === 0) return;

        if (!cartData || !cartData.cartItems) {
            alert("Cart is empty or not loaded yet.");
            return;
        }

        const cartItem = cartData.cartItems.find(item => item.dishId === dishId);
        if (!cartItem) return alert("Item not found");

        const newQty = currentQty - 1;

        if (newQty === 0) {
            setItemToRemove({ dishId, cartItem });
            setShowRemoveConfirm(true);
            return;
        }

        await updateQuantity(dishId, newQty);
    };
    const confirmRemove = async () => {
        if (itemToRemove) {
            await updateQuantity(itemToRemove.dishId, 0);
            setShowRemoveConfirm(false);
            setItemToRemove(null);
        }
    };

    const cancelRemove = () => {
        setShowRemoveConfirm(false);
        setItemToRemove(null);
    };


    // const displayData = cartData.cartItems || [];
    // const hasItems = displayData.filter(item => (quantities[item.dishId] ?? item.quantity ?? 1) > 0).length > 0;
    const displayData = cartData.cartItems || [];
    const hasItems = displayData.filter(item => (quantities[item.dishId] ?? item.quantity ?? 1) > 0).length > 0;
    if (!hasItems && !showReceipt) {
        return null; // Don't render anything when cart is empty and not showing receipt
    }

    return (
        <div style={{
            background: '#b15cd6',
            borderRadius: '0.5rem',
            padding: '1rem',
            height: 'fit-content',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            marginTop: window.innerHeight <= 700 ? "3rem" : "5rem",
            minHeight: '500px',
            position: 'sticky',
            top: '6rem',
            zIndex: window.innerHeight <= 700 ? 30000 : " ",
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '0.3rem',
                paddingBottom: '0.75rem',
                borderBottom: '0px solid #e9ecef',
                flexShrink: 0,
                position: 'relative',
                height: '40px'
            }}>
                {/* Show different title based on state */}
                <h4 style={{
                    color: '#f8f9fa',
                    margin: 0,
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    textAlign: 'center'
                }}>
                    {showReceipt ? 'Order Receipt' : 'Cart'}
                </h4>

                {/* Show clear button only when cart has items and not showing receipt */}
                {hasItems && !showReceipt && (
                    <button
                        onClick={clearCart}
                        style={{
                            background: '#e53e3e',
                            border: '1px solid #e53e3e',
                            color: 'white',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.2s ease',
                            position: 'absolute',
                            right: "10px",
                            top: '50%',
                            transform: 'translateY(-75%)'
                        }}
                    >
                        Clear
                    </button>
                )}

                {/* Show back button when showing receipt */}
                {showReceipt && (
                    <>

                        <button
                            onClick={closeReceipt}
                            style={{
                                background: 'transparent',
                                border: '1px solid #f8f9fa',
                                color: '#f8f9fa',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: window.innerWidth <= 1400 ? "none" : "flex",
                                alignItems: 'center',
                                gap: '0.3rem',
                                transition: 'all 0.2s ease',
                                position: 'absolute',
                                left: "10px",
                                top: '50%',
                                transform: 'translateY(-75%)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.color = '#b15cd6';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#f8f9fa';
                            }}
                        >
                            ← Back
                        </button>
                        <button
                            onClick={closeReceipt}
                            style={{
                                background: 'transparent',
                                border: '1px solid #f8f9fa',
                                color: '#f8f9fa',
                                padding: '0.4rem 0.8rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: window.innerWidth <= 1400 ? "flex" : "none",
                                alignItems: 'center',
                                gap: '0.3rem',
                                transition: 'all 0.2s ease',
                                position: 'absolute',
                                left: "0px",
                                top: '50%',
                                transform: 'translateY(-75%)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.color = '#b15cd6';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#f8f9fa';
                            }}
                        >
                            ←
                        </button>
                    </>

                )}
            </div>

            {/* Cart Content Container */}
            <div className="cart-content-container hide-scrollbar">
                {showReceipt ? (
                    // RECEIPT VIEW
                    <div ref={receiptRef} style={{
                        backgroundColor: "white",
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        overflowY: 'auto',
                        maxHeight: 'calc(85vh - 100px)',
                        scrollbarWidth: "none",
                    }}>
                        <div ref={printRef}>
                            {/* Payment Success Header */}
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #b15cd6'
                            }}>
                                <h5 style={{
                                    color: '#b15cd6',
                                    margin: '0.5rem 0',
                                    fontSize: window.innerWidth < 1200 ? '1.1rem' : '1.2rem'
                                }}>
                                    ✅ Payment Successful!
                                </h5>
                                <h6 style={{
                                    margin: 0,
                                    color: '#2d3748',
                                    fontSize: '1rem'
                                }}>
                                    {bannerData?.companyName || "MAAMAAS HOUSE"}
                                </h6>
                            </div>

                            {/* Order Details */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                marginBottom: '1rem',
                                fontSize: '0.9rem'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Order ID:</span>
                                    <span className='endalign' style={{ fontWeight: '700', color: '#2d3748' }}>{orderData.orderId}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Date:</span>
                                    <span className='endalign' style={{ fontWeight: '700', color: '#2d3748' }}>
                                        {orderData.date ? new Date(orderData.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Order Type:</span>
                                    <span className='endalign' style={{ fontWeight: '700', color: '#2d3748' }}>{orderData.orderType}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Time:</span>
                                    <span className='endalign' style={{ fontWeight: '700', color: '#2d3748' }}>
                                        {orderData.time ? new Date(`1970-01-01T${orderData.time}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : "N/A"}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: '600', color: '#4a5568' }}>Payment Method:</span>
                                    <span style={{ fontWeight: '700', color: '#2d3748', display: window.innerWidth < 1200 ? "flex" : "flex", alignItems: window.innerWidth < 1200 ? "end" : " " }}>{orderData.paymentMethod}</span>
                                </div>
                            </div>

                            <hr style={{ border: '1px dashed #cbd5e0', margin: '1rem 0' }} />

                            {/* Order Items Table */}
                            {Array.isArray(orderData.order) && orderData.order.length > 0 ? (
                                <>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '0.85rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                                <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600', color: '#4a5568' }}>Item</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', color: '#4a5568' }}>Qty</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#4a5568' }}>Price</th>
                                                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', color: '#4a5568' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderData.order.map((item, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                    <td style={{ padding: '0.5rem', textAlign: 'left' }}>{item.dishName || "N/A"}</td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>{item.quantity ?? "N/A"}</td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>₹{item.price?.toFixed(2) || "0.00"}</td>
                                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                                        ₹{item.price && item.quantity ? (item.price * item.quantity).toFixed(2) : "0.00"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Charges Breakdown */}
                                    <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                            <span>Sub Total:</span>
                                            <span>₹{orderData.subTotal?.toFixed(2) || "0.00"}</span>
                                        </div>
                                        {orderData.platformCharges > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span>Platform Charges:</span>
                                                <span>₹{orderData.platformCharges?.toFixed(2) || "0.00"}</span>
                                            </div>
                                        )}
                                        {orderData.serviceCharge > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span>Service Charges:</span>
                                                <span>₹{orderData.serviceCharge?.toFixed(2) || "0.00"}</span>
                                            </div>
                                        )}
                                        {orderData.cgst > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span>CGST:</span>
                                                <span>₹{orderData.cgst?.toFixed(2) || "0.00"}</span>
                                            </div>
                                        )}
                                        {orderData.sgst > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                                <span>SGST:</span>
                                                <span>₹{orderData.sgst?.toFixed(2) || "0.00"}</span>
                                            </div>
                                        )}
                                    </div>

                                    <hr style={{ border: '1px dashed #cbd5e0', margin: '1rem 0' }} />

                                    {/* Grand Total */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '0.75rem',
                                        backgroundColor: '#f7fafc',
                                        borderRadius: '0.25rem',
                                        border: '0px solid #b15cd6',
                                        marginBottom: '1rem'
                                    }}>
                                        <span style={{ fontSize: '1.1rem', fontWeight: '700', color: '#2d3748' }}>Grand Total:</span>
                                        <span style={{ fontSize: '1.3rem', fontWeight: '900', color: '#b15cd6' }}>
                                            ₹{orderData.grandTotal?.toFixed(2) || "0.00"}
                                        </span>
                                    </div>

                                    {/* Thank You Message */}
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '1rem',
                                        borderTop: '2px solid #e2e8f0',
                                        marginTop: '1rem'
                                    }}>
                                        <h6 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>
                                            Thank You! Visit Again
                                        </h6>
                                        <p style={{ color: '#a0aec0', fontSize: '0.8rem', margin: 0 }}>
                                            www.maamaas.com
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <p style={{ textAlign: 'center', color: '#718096' }}>No order data available.</p>
                            )}
                        </div>

                        {/* Receipt Action Buttons */}
                        <div className="receipt-buttons" style={{
                            display: 'flex',
                            gap: window.innerWidth < 1200 ? '0.5rem' : '1rem',
                            justifyContent: 'center',
                            marginTop: '1rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e2e8f0'
                        }}>
                            <button
                                onClick={handlePrint}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: window.innerWidth < 1200 ? '0.3rem 1rem ' : '0.75rem 1.5rem',
                                    backgroundColor: '#b15cd6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#9b46c9'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#b15cd6'}
                            >
                                <FaPrint /> Print
                            </button>
                            <button
                                onClick={handleDownload}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: window.innerWidth < 1200 ? '0.5rem 1rem ' : '0.75rem 1.5rem',
                                    backgroundColor: '#4a5568',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#2d3748'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#4a5568'}
                            >
                                <FaDownload /> Download
                            </button>
                        </div>
                    </div>
                ) : hasItems ? (
                    // CART VIEW
                    <>
                        <div style={{
                            backgroundColor: "white",
                            border: "1px solid #e9ecef",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                        }}>
                            {/* Cart Items List */}
                            {displayData
                                .filter(item => (quantities[item.dishId] ?? item.quantity ?? 1) > 0)
                                .map((item, index) => {
                                    const currentQuantity = quantities[item.dishId] ?? item.quantity ?? 1;

                                    return (
                                        <div key={item.cartItemId || index} className="cart-item-box">
                                            <div className="cart-item-row">
                                                <div className="item-name-container">
                                                    <div className="item-name" title={item.dishName}>
                                                        {item.dishName}
                                                    </div>
                                                </div>
                                                <div className="item-controls-container">
                                                    <div className="quantity-controls">
                                                        <button
                                                            className="quantity-btn outline-btn"
                                                            onClick={() => decreaseQuantity(item.dishId)}
                                                            title="Decrease quantity"
                                                        >
                                                            <FaMinus className="quantity-icon" />
                                                        </button>
                                                        <span className="quantity-display">
                                                            {currentQuantity}
                                                        </span>
                                                        <button
                                                            className="quantity-btn outline-btn"
                                                            onClick={() => increaseQuantity(item.dishId)}
                                                            title="Increase quantity"
                                                        >
                                                            <FaPlus className="quantity-icon" />
                                                        </button>
                                                    </div>
                                                    <div className="item-price">
                                                        ₹{item.totalPrice?.toFixed(2) || '0.00'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                            {/* Order Summary */}
                            <div className="total-section">
                                <div style={{
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#2d3748',
                                    marginBottom: '1rem',
                                    textAlign: 'center'
                                }}>
                                    Order Summary
                                </div>

                                {/* Price Breakdown */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '0.5rem',
                                        fontSize: '1rem',
                                        color: '#4a5568'
                                    }}>
                                        <span>Sub Total:</span>
                                        <span>₹{cartData.subtotal?.toFixed(2) || '0.00'}</span>
                                    </div>

                                    {cartData.platformCharges > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.5rem',
                                            fontSize: '0.9rem',
                                            color: '#4a5568'
                                        }}>
                                            <span>Platform Charges:</span>
                                            <span>₹{cartData.platformCharges?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    )}

                                    {cartData.packingTotal > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.5rem',
                                            fontSize: '1rem',
                                            color: '#4a5568'
                                        }}>
                                            <span>Packing Charges:</span>
                                            <span>₹{cartData.packingTotal?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    )}

                                    {cartData.serviceCharges > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.5rem',
                                            fontSize: '1rem',
                                            color: '#4a5568'
                                        }}>
                                            <span>Service Charges:</span>
                                            <span>₹{cartData.serviceCharges?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    )}

                                    {cartData.gstTotal > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '0.75rem',
                                            fontSize: '1rem',
                                            color: '#4a5568'
                                        }}>
                                            <span>GST:</span>
                                            <span>₹{cartData.gstTotal?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        borderTop: '1px solid #e2e8f0',
                                        margin: '0.75rem 0'
                                    }}></div>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: window.innerHeight <= 700 ? '1rem' : '1.1rem',
                                        fontWeight: '700',
                                        color: '#2d3748',
                                        paddingTop: '0.5rem'
                                    }}>
                                        <span>Grand Total:</span>
                                        <span style={{
                                            color: '#b15cd6',
                                            fontSize: window.innerWidth < 1200 ? '1rem' : '1.3rem'
                                        }}>
                                            ₹{grandTotal.toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                <div style={{
                                    fontSize: '0.8rem',
                                    color: '#718096',
                                    textAlign: 'center',
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    backgroundColor: '#f7fafc',
                                    borderRadius: '0.25rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    {cartData.orderType === 'DINE_IN' ? 'Dine In Order' : 'Take Away Order'}
                                </div>
                            </div>
                        </div>

                        {/* Payment Method Selection */}
                        <div style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.25rem',
                            backgroundColor: 'white'
                        }}>
                            <div style={{
                                fontSize: '1rem',
                                fontWeight: '600',
                                color: '#2d3748',
                                marginBottom: '0.5rem'
                            }}>
                                Select Payment Method
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="Cash"
                                        checked={selectedPaymentMethod === "Cash"}
                                        onChange={() => handlePaymentMethodChange("Cash")}
                                    />
                                    <span>Cash</span>
                                </label>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="Online_Payment"
                                        checked={selectedPaymentMethod === "Online_Payment"}
                                        onChange={() => handlePaymentMethodChange("Online_Payment")}
                                    />
                                    <span>Online</span>
                                </label>
                            </div>
                        </div>

                        {/* Place Order Button - Only show for Cash payment */}
                        {selectedPaymentMethod === "Cash" && (
                            <button
                                onClick={() => handlePlaceOrder("Cash")}
                                style={{
                                    width: '100%',
                                    backgroundColor: 'white',
                                    color: 'black',
                                    fontWeight: '700',
                                    fontSize: '1rem',
                                    padding: '12px',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    marginTop: '1rem',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                            >
                                Place Order
                            </button>
                        )}

                        {/* Loading indicator for Online Payment */}
                        {selectedPaymentMethod === "Online_Payment" && loading && (
                            <div style={{
                                textAlign: 'center',
                                padding: '1rem',
                                color: '#4a5568',
                                fontSize: '0.9rem'
                            }}>
                                Loading payment gateway...
                            </div>
                        )}
                    </>
                ) : (
                    // EMPTY CART
                    <div className="empty-cart">
                        Cart is empty
                    </div>
                )}
            </div>

            {/* Remove Confirmation Modal (keep existing) */}
            {/* ... existing modal code ... */}

            <style>{`
                /* Keep all your existing CSS styles */
                .cart-item-box {
                    padding: 0.5rem 1rem;
                    border-bottom: 0px solid #e9ecef;
                }
                
                .cart-item-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 1rem;
                }
                
                .item-name-container {
                    flex: 2;
                    min-width: 0;
                }
                
                .item-name {
                    font-weight: 500;
                    color: #2d3748;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                }
                
                .item-controls-container {
                    flex: 1;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    min-width: 150px;
                }
                
                .quantity-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .quantity-btn {
                    width: 24px;
                    height: 24px;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                
                .outline-btn {
                    border: 1px solid #b15cd6;
                    background: white;
                    color: #b15cd6;
                }
                
                .outline-btn:hover {
                    background: #b15cd6;
                    color: white;
                }
                
                .quantity-display {
                    min-width: 20px;
                    text-align: center;
                    font-weight: 600;
                    color: #2d3748;
                }
                
                .item-price {
                    font-weight: 600;
                    color: #2d3748;
                    min-width: 60px;
                    text-align: right;
                }
                
                .cart-content-container {
                    flex: 1;
                    overflow-y: auto;
                    padding-right: 4px;
                }
                
                .hide-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                
                .hide-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                
                .hide-scrollbar::-webkit-scrollbar-thumb {
                    background: #b15cd6;
                    border-radius: 4px;
                }
                
                .empty-cart {
                    text-align: center;
                    padding: 3rem 1rem;
                    color: #718096;
                    font-size: 1.1rem;
                    background: white;
                    border-radius: 0.5rem;
                    border: 1px solid #e9ecef;
                }
                
                .total-section {
                    padding: 1rem;
                    border-top: 0px solid #e9ecef;
                }
                
                /* Responsive styles */
                @media (max-width: 1399px) {
                    .cart-item-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                    
                    .item-name-container {
                        width: 100%;
                        margin-bottom: 0rem;
                    }
                    
                    .item-name {
                        font-size: 13px;
                        width: 100%;
                    }
                    
                    .item-controls-container {
                        width: 100%;
                        justify-content: space-between;
                        min-width: auto;
                    }
                    
                    .quantity-controls {
                        margin-left: 0;
                    }
                    
                    .quantity-btn {
                        width: 22px;
                        height: 22px;
                    }
                    
                    .quantity-icon {
                        width: 9px;
                        height: 9px;
                    }
                    
                    .quantity-display {
                        font-size: 13px;
                        min-width: 18px;
                    }
                    
                    .item-price {
                        font-size: 13px;
                        min-width: 70px;
                        text-align: right;
                        margin-left: auto;
                    }
                }

                @media (max-width: 768px) {
                    .cart-item-box {
                        padding: 0.5rem;
                    }
                    
                    .quantity-btn {
                        width: 20px;
                        height: 20px;
                    }
                    
                    .quantity-icon {
                        width: 8px;
                        height: 8px;
                    }
                    
                    .quantity-display {
                        min-width: 16px;
                        font-size: 12px;
                    }
                    
                    .item-price {
                        min-width: 55px;
                        font-size: 12px;
                    }
                    
                    .item-name {
                        font-size: 12px;
                    }
                }
                    @media(max-width:1200px) {
                    .endalign{
                    display:flex,
                    text-align:end,
                    }
                    }
            `}</style>
        </div>
    );
};

export default MenuCartSidebar;